import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, Drawer, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import type { IBrandReview } from '../../redux/buzzbridge/brandReview.types';
import type { ICampaignBrandPopulated } from '../../redux/buzzbridge/types';
import { fetchBrandReviewsPage } from '../../redux/buzzbridge/actions';
import { useDispatchPromise } from '../../utils/AppUtils';
import type { IBrandReviewsPage } from '../../utils/brandReviewsApi';

const PAGE_SIZE = 10;

function formatRating(n?: number | null): string {
    if (n == null || !Number.isFinite(Number(n))) return '—';
    return Number(n).toFixed(1);
}

function reviewerLabel(review: IBrandReview): string {
    const influencer = review.influencer as { name?: string; title?: string; username?: string } | string | undefined;
    if (influencer && typeof influencer === 'object') {
        if (influencer.name?.trim()) return influencer.name.trim();
        if (influencer.title?.trim()) return influencer.title.trim();
        if (influencer.username?.trim()) return `@${influencer.username.trim()}`;
    }
    const u = review.user as { firstName?: string; lastName?: string; userName?: string } | string | undefined;
    if (u && typeof u === 'object') {
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
        if (name) return name;
        if (u.userName) return `@${u.userName}`;
    }
    return 'Creator';
}

function reviewerAvatar(review: IBrandReview): string | undefined {
    const u = review.user as { profile_picture?: string } | string | undefined;
    if (u && typeof u === 'object' && u.profile_picture) return u.profile_picture;
    return undefined;
}

function normalizeWebsite(url?: string): string | undefined {
    const raw = url?.trim();
    if (!raw) return undefined;
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://${raw}`;
}

export interface BrandReviewsDrawerProps {
    open: boolean;
    onClose: () => void;
    brand: ICampaignBrandPopulated;
}

export const BrandReviewsDrawer: React.FC<BrandReviewsDrawerProps> = ({ open, onClose, brand }) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const dispatchPromise = useDispatchPromise();
    const [reviews, setReviews] = useState<IBrandReview[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(brand.number_of_reviews ?? 0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const brandId = brand._id;
    const rating = brand.rating;
    const reviewCount = totalCount || brand.number_of_reviews || 0;
    const brandAvatar = brand.profile_picture || brand.logo;
    const featureImage = brand.feature_image || brand.logo || brand.profile_picture;
    const description = brand.description?.trim();
    const websiteHref = normalizeWebsite(brand.website);

    const loadPage = useCallback(
        async (nextPage: number, append: boolean) => {
            if (!brandId) return;
            if (append) setLoadingMore(true);
            else setLoading(true);
            setError(null);
            try {
                const result = (await dispatchPromise(
                    fetchBrandReviewsPage({ brandId, page: nextPage, limit: PAGE_SIZE }),
                )) as IBrandReviewsPage;
                setReviews(prev => (append ? [...prev, ...result.reviews] : result.reviews));
                setPage(result.currentPage);
                setTotalPages(result.totalPages);
                setTotalCount(result.totalCount);
            } catch {
                setError('Unable to load reviews right now.');
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [brandId, dispatchPromise],
    );

    useEffect(() => {
        if (!open || !brandId) return;
        setReviews([]);
        setPage(1);
        setTotalPages(1);
        setTotalCount(brand.number_of_reviews ?? 0);
        void loadPage(1, false);
    }, [open, brandId, brand.number_of_reviews, loadPage]);

    const hasMore = page < totalPages;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 'min(420px, 100vw)' },
                    maxWidth: '100%',
                    bgcolor: t.deep_grey || '#121212',
                    borderLeft: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                {featureImage ? (
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: { xs: 180, sm: 200 },
                            overflow: 'hidden',
                        }}>
                        <Box
                            component="img"
                            src={featureImage}
                            alt=""
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(18,18,18,0.2) 45%, rgba(18,18,18,0.95) 100%)',
                            }}
                        />
                    </Box>
                ) : (
                    <Box sx={{ height: 56 }} />
                )}

                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Close reviews"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: '#fff',
                        bgcolor: 'rgba(0,0,0,0.45)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                    }}>
                    <CloseIcon />
                </IconButton>

                <Box
                    sx={{
                        px: 2.5,
                        pt: featureImage ? 0 : 2,
                        pb: 2,
                        mt: featureImage ? -5 : 0,
                        position: 'relative',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
                        {brandAvatar ? (
                            <Box
                                component="img"
                                src={brandAvatar}
                                alt=""
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 1.5,
                                    objectFit: 'cover',
                                    border: '2px solid rgba(255,255,255,0.12)',
                                    bgcolor: t.card_grey,
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <Avatar
                                variant="rounded"
                                sx={{
                                    width: 52,
                                    height: 52,
                                    bgcolor: 'rgba(255,193,7,0.15)',
                                    color: t.highlight,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                }}>
                                {(brand.name || '?').slice(0, 1)}
                            </Avatar>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 18,
                                    color: t.true_white,
                                    lineHeight: 1.2,
                                }}>
                                {brand.name}
                            </Typography>
                            {brand.industry ? (
                                <Typography sx={{ fontSize: 12, color: t.subtle_white, mt: 0.35 }}>
                                    {brand.industry}
                                </Typography>
                            ) : (
                                <Typography sx={{ fontSize: 12, color: t.subtle_white, mt: 0.35 }}>
                                    Campaign host
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mb: description || websiteHref ? 1.5 : 0,
                        }}>
                        <StarRoundedIcon sx={{ fontSize: 20, color: t.highlight }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 16,
                                color: t.highlight,
                                lineHeight: 1,
                            }}>
                            {reviewCount > 0 ? formatRating(rating) : '—'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: t.subtle_white }}>
                            {reviewCount > 0
                                ? `${reviewCount} review${reviewCount === 1 ? '' : 's'}`
                                : 'No reviews yet'}
                        </Typography>
                    </Box>

                    {description ? (
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: t.tag_white || 'rgba(255,255,255,0.72)',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                lineHeight: 1.5,
                                mb: websiteHref ? 1 : 0,
                            }}>
                            {description}
                        </Typography>
                    ) : null}

                    {websiteHref ? (
                        <Link
                            href={websiteHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                                fontSize: 12,
                                color: t.highlight,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                wordBreak: 'break-all',
                            }}>
                            {brand.website?.trim() || websiteHref}
                        </Link>
                    ) : null}
                </Box>
            </Box>

            <Box
                sx={{
                    px: 2.5,
                    pb: 1,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: t.subtle_white,
                    }}>
                    Reviews
                </Typography>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={28} sx={{ color: t.highlight }} />
                    </Box>
                )}

                {!loading && error && (
                    <Typography sx={{ fontSize: 14, color: t.subtle_white, textAlign: 'center', py: 6 }}>
                        {error}
                    </Typography>
                )}

                {!loading && !error && reviews.length === 0 && (
                    <Typography sx={{ fontSize: 14, color: t.subtle_white, textAlign: 'center', py: 6 }}>
                        No reviews yet for this brand.
                    </Typography>
                )}

                {!loading && reviews.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {reviews.map((review, i) => (
                            <Box
                                key={String(review._id || `${reviewerLabel(review)}-${i}`)}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: t.card_grey,
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        mb: 0.75,
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                        <Avatar
                                            src={reviewerAvatar(review)}
                                            alt=""
                                            sx={{ width: 32, height: 32, bgcolor: 'rgba(255,193,7,0.15)' }}>
                                            {reviewerLabel(review).slice(0, 1)}
                                        </Avatar>
                                        <Typography
                                            noWrap
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 14,
                                                color: t.true_white,
                                            }}>
                                            {reviewerLabel(review)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
                                        <StarRoundedIcon sx={{ fontSize: 18, color: t.highlight }} />
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 15,
                                                color: t.highlight,
                                                lineHeight: 1,
                                            }}>
                                            {formatRating(review.rating)}
                                        </Typography>
                                    </Box>
                                </Box>
                                {typeof review.review_text === 'string' && review.review_text.trim() ? (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: t.tag_white || 'rgba(255,255,255,0.7)',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            lineHeight: 1.45,
                                        }}>
                                        {review.review_text.trim()}
                                    </Typography>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                )}

                {hasMore && !loading && !error && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2.5, pb: 1 }}>
                        <Button
                            onClick={() => void loadPage(page + 1, true)}
                            disabled={loadingMore}
                            sx={{
                                textTransform: 'none',
                                color: t.highlight,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                            }}>
                            {loadingMore ? <CircularProgress size={18} sx={{ color: t.highlight }} /> : 'Load more'}
                        </Button>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};
